import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bus, Download, Navigation } from 'lucide-react';
import { ButtonPanel } from '../ButtonPanel';
import packageJson from '../../../package.json';

export const HomeScreen = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mostrarBotonInstalar, setMostrarBotonInstalar] = useState(false);
  const [obtieniendoUbicacion, setObtieniendoUbicacion] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMostrarBotonInstalar(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setMostrarBotonInstalar(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const instalarPWA = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setMostrarBotonInstalar(false);
    }
    
    setDeferredPrompt(null);
  };

  // Obtener paradas cercanas con geolocalización
  const obtenerParadasCercanas = async () => {
    if (!navigator.geolocation) {
      setError('Tu dispositivo no soporta geolocalización');
      return;
    }

    setObtieniendoUbicacion(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        navigate('/near-stops', {
          state: {
            latitude,
            longitude,
          }
        });
      
        setObtieniendoUbicacion(false);

      },
      (error) => {
        setObtieniendoUbicacion(false);
        let mensaje = 'No se pudo obtener tu ubicación';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            mensaje = 'Debes permitir el acceso a tu ubicación';
            break;
          case error.POSITION_UNAVAILABLE:
            mensaje = 'Ubicación no disponible';
            break;
          case error.TIMEOUT:
            mensaje = 'Tiempo de espera agotado';
            break;
          default:
            mensaje = 'No se pudo obtener tu ubicación';
            break;
        }
        
        setError(mensaje);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };


  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Bus className="text-blue-500" size={32} />
          <h1 className="text-4xl font-bold text-gray-900">Mi Bondi</h1>
        </div>
        <p className="text-gray-600">Gestiona tus paradas favoritas</p>
      </div>

      {/* Install PWA */}
      {mostrarBotonInstalar && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-6 flex gap-3">
          <button
            onClick={instalarPWA}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition font-semibold"
          >
            <Download size={18} />
            Instalar Aplicación
          </button>
        </div>
      )}

      {/* Main Actions */}
      <div className="grid gap-4">
        <ButtonPanel
          onClick={() => navigate('/folders')}
          title="Mis Carpetas"
          subtitle="Ver todas tus paradas organizadas"
          color="border-l-blue-500"
          leftIcon="📁"
        />
        
        <ButtonPanel
          onClick={() => navigate('/add-folder')}
          title="Nueva Carpeta"
          subtitle="Crear un nuevo grupo de paradas"
          color="border-l-green-500"
          leftIcon="➕"
        />

        <button onClick={obtenerParadasCercanas} disabled={obtieniendoUbicacion} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 px-6 rounded-xl transition duration-200 flex items-center justify-center gap-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
          <Navigation className={`w-5 h-5 ${obtieniendoUbicacion ? 'animate-pulse' : ''}`} />
          {obtieniendoUbicacion ? 'OBTENIENDO UBICACIÓN...' : 'PARADAS CERCANAS'}
        </button>
      </div>


      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded-lg mt-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-gray-500 text-sm">
        <p>Mi Bondi v{packageJson.version} - By Diegui</p>
      </div>
    </div>
  );
};
